import type { AlignmentViolation } from './alignment-form-mutations';

/**
 * DEV-269 — tradução das `violations` do 422 `alignment_form_invalid` para a
 * pessoa consultora (QA 09/10: uma regra de Lógica sobre um campo protegido era
 * recusada pelo servidor e a tela dizia só "Algo deu errado").
 *
 * Cada mensagem nomeia o campo/página pelo TÍTULO (nome como reserva) para a
 * pessoa saber o que corrigir — AC-41 / SC-14.
 */

interface NamedElement {
  name?: unknown;
  title?: unknown;
  elements?: unknown;
  templateElements?: unknown;
}
interface DefinitionLike {
  pages?: unknown;
}

function collectTitles(elements: unknown, out: Map<string, string>): void {
  if (!Array.isArray(elements)) return;
  for (const raw of elements as NamedElement[]) {
    if (typeof raw.name === 'string') {
      out.set(raw.name, typeof raw.title === 'string' && raw.title ? raw.title : raw.name);
    }
    collectTitles(raw.elements, out);
    collectTitles(raw.templateElements, out);
  }
}

/** Mapa name → título de toda página e pergunta da definição (aninhadas inclusive). */
export function titlesByName(definition: unknown): Map<string, string> {
  const out = new Map<string, string>();
  const pages = (definition as DefinitionLike | null | undefined)?.pages;
  if (!Array.isArray(pages)) return out;
  for (const page of pages as NamedElement[]) {
    if (typeof page.name === 'string') {
      out.set(page.name, typeof page.title === 'string' && page.title ? page.title : page.name);
    }
    collectTitles(page.elements, out);
  }
  return out;
}

const PROTECTED_HINT =
  'não pode ficar oculto — nem pela página, nem por uma regra de Lógica — e precisa continuar obrigatório.';

/** Mensagem de UMA violação; `null` para código desconhecido. */
export function describeViolation(
  v: AlignmentViolation,
  titles: Map<string, string>
): string | null {
  const label = v.name ? `"${titles.get(v.name) ?? v.name}"` : null;
  switch (v.code) {
    case 'protected_hidden':
      return `${label ?? 'Um campo protegido'} é um campo protegido e ${PROTECTED_HINT}`;
    case 'protected_optional':
      return `${label ?? 'Um campo protegido'} é um campo protegido e precisa continuar obrigatório.`;
    case 'descritivos_hidden':
      return 'A página "Descritivos" contém os campos que descrevem a vaga e não pode ficar oculta.';
    case 'default_question_modified':
      return `${label ?? 'Uma pergunta padrão'} é uma pergunta padrão: só a visibilidade e a obrigatoriedade podem mudar (confira as regras de Lógica e as opções).`;
    case 'default_page_modified':
      return `${label ?? 'Uma página padrão'} é uma página padrão: só a visibilidade pode mudar.`;
    case 'default_question_moved':
      return `${label ?? 'Uma pergunta padrão'} precisa continuar na página e na posição de origem.`;
    case 'question_in_default_page':
      return `${label ?? 'Uma pergunta nova'} não pode entrar numa página padrão: crie uma página adicional para ela.`;
    case 'default_page_order':
      return 'As páginas padrão precisam continuar na ordem original.';
    case 'additional_page_out_of_band':
      return `${label ?? 'Uma página adicional'} só pode ficar entre "Perfil do candidato" e "Descritivos".`;
    case 'default_page_missing':
    case 'default_question_missing':
      return `${label ?? 'Um elemento padrão'} é parte da estrutura padrão e não pode ser removido.`;
    case 'duplicate_name':
      return `${label ?? 'Um elemento'} tem o mesmo nome de outro elemento: nomes precisam ser únicos.`;
    default:
      return null;
  }
}

/**
 * Texto do painel inline para o 422: uma frase por violação (sem repetir),
 * encabeçada por "Não foi possível salvar". `null` quando nenhuma violação é
 * explicável (o container cai no Sonner genérico).
 */
export function explainViolations(
  violations: readonly AlignmentViolation[],
  definition: unknown
): string | null {
  const titles = titlesByName(definition);
  const lines: string[] = [];
  for (const v of violations) {
    const text = describeViolation(v, titles);
    if (text && !lines.includes(text)) lines.push(text);
  }
  if (lines.length === 0) return null;
  return `Não foi possível salvar. ${lines.join(' ')}`;
}
